import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import HumanCheck from '../components/HumanCheck';
import { createApiUrl } from '../utils/apiConfig';
import styles from './Register.module.css';
import { Link } from 'react-router-dom';

const RegisterOrg = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [formData, setFormData] = useState({
    name: '',
    street: '',
    city: '',
    postcode: '',
    country: 'United Kingdom',
    contactName: '',
    email: '',
    contactPhone: '',
    password: '',
    confirmPassword: ''
  });
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [humanCheckValid, setHumanCheckValid] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const errorMessage = urlParams.get('error');
    if (errorMessage) {
      alert(decodeURIComponent(errorMessage));
    }
  }, [location]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCountryChange = (e) => {
    setFormData(prev => ({
      ...prev,
      country: e.target.value
    }));
  };

  const handleEmailBlur = async () => {
    if (formData.email) {
      try {
        const response = await axios.post(createApiUrl('/api/check-email'), { email: formData.email });
        if (response.data.exists) {
          setErrors(prev => ({
            ...prev,
            email: 'Email already exists. Please use a different email.'
          }));
        } else {
          setErrors(prev => ({
            ...prev,
            email: ''
          }));
        }
      } catch (error) {
        console.error('Error checking email:', error);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = {};

    if (formData.password !== formData.confirmPassword) {
      newErrors.password = 'Passwords do not match. Please try again.';
    }
    if (formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters long.';
    }

    if (!humanCheckValid) {
      newErrors.humanCheck = 'Please answer the human verification question correctly.';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    try {
      const response = await axios.post(createApiUrl('/api/org-register'), formData);
      if (response.data.success) {
        // Show success message and redirect to login
        setSuccessMessage(response.data.message || 'Registration completed successfully!');
        setTimeout(() => {
          navigate('/login');
        }, 2000); // Redirect after 2 seconds
      }
    } catch (error) {
      setErrors({
        submit: error.response?.data?.message || 'An error occurred during registration.'
      });
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  return (
    <div className={styles.registerSchoolContainer}>
      <h1 className={styles.registerSchoolHeading}>Register<br />Organization</h1>
      <div className={styles.registerSchoolCard}>
        {successMessage && <div className="alert alert-success" style={{backgroundColor: '#d4edda', color: '#155724', padding: '12px', borderRadius: '4px', marginBottom: '16px', border: '1px solid #c3e6cb'}}>{successMessage}</div>}
        {errors.submit && <div className="alert alert-danger">{errors.submit}</div>}
        <form id="registerForm" onSubmit={handleSubmit} className={styles.registerForm}>
          <div className={styles.formField}>
            <input
              type="text"
              id="name"
              name="name"
              className={styles.formInput}
              required
              placeholder="Organization Name"
              value={formData.name}
              onChange={handleChange}
            />
            <label htmlFor="name" className={styles.formLabel}>Organization Name:</label>
          </div>

          <div className={styles.formField}>
            <select
              id="country"
              name="country"
              className={styles.formSelect}
              required
              value={formData.country}
              onChange={handleCountryChange}
            >
              <option value="United Kingdom">United Kingdom</option>
              <option value="Africa">Africa</option>
              <option value="Canada">Canada</option>
              <option value="Caribbean">Caribbean</option>
              <option value="Europe">Europe</option>
              <option value="United States">United States</option>
            </select>
            <label htmlFor="country" className={styles.formLabel}>Country:</label>
          </div>

          <div className={styles.formField}>
            <input
              type="text"
              id="postcode"
              name="postcode"
              className={styles.formInput}
              required
              placeholder="Postcode/Zip Code"
              value={formData.postcode}
              onChange={handleChange}
            />
            <label htmlFor="postcode" className={styles.formLabel}>Postcode/Zip Code:</label>
          </div>

          <div className={styles.formField}>
            <input
              type="text"
              id="street"
              name="street"
              className={styles.formInput}
              required
              placeholder="Street"
              value={formData.street}
              onChange={handleChange}
            />
            <label htmlFor="street" className={styles.formLabel}>Street:</label>
          </div>

          <div className={styles.formField}>
            <input
              type="text"
              id="city"
              name="city"
              className={styles.formInput}
              required
              placeholder="City"
              value={formData.city}
              onChange={handleChange}
            />
            <label htmlFor="city" className={styles.formLabel}>City:</label>
          </div>

          <div className={styles.formField}>
            <input
              type="text"
              id="contactName"
              name="contactName"
              className={styles.formInput}
              required
              placeholder="Contact Name"
              value={formData.contactName}
              onChange={handleChange}
            />
            <label htmlFor="contactName" className={styles.formLabel}>Contact Name:</label>
          </div>

          <div className={styles.formField}>
            <input
              type="email"
              id="email"
              name="email"
              className={`${styles.formInput} ${errors.email ? styles.humanCheckError : ''}`}
              required
              placeholder="Organization Email"
              value={formData.email}
              onChange={handleChange}
              onBlur={handleEmailBlur}
            />
            <label htmlFor="email" className={styles.formLabel}>Organization Email:</label>
            {errors.email && (
              <div className={styles.errorText}>{errors.email}</div>
            )}
          </div>

          <div className={styles.formField}>
            <input
              type="text"
              id="contactPhone"
              name="contactPhone"
              className={styles.formInput}
              required
              placeholder="Organization Phone"
              value={formData.contactPhone}
              onChange={handleChange}
            />
            <label htmlFor="contactPhone" className={styles.formLabel}>Organization Phone:</label>
          </div>

          <div className={`${styles.formField} ${styles.positionRelative}`}>
            <input
              type={showPassword ? 'text' : 'password'}
              id="password"
              name="password"
              className={`${styles.formInput} ${errors.password ? styles.humanCheckError : ''}`}
              required
              placeholder="Password"
              value={formData.password}
              onChange={handleChange}
            />
            <label htmlFor="password" className={styles.formLabel}>Password:</label>
            <button type="button" className={styles.togglePassword} onClick={togglePasswordVisibility} tabIndex={-1}>
              <i className={`fas fa-${showPassword ? 'eye' : 'eye-slash'}`}></i>
            </button>
            {errors.password && (
              <div className={styles.errorText}>{errors.password}</div>
            )}
          </div>

          <div className={`${styles.formField} ${styles.positionRelative}`}>
            <input
              type={showConfirmPassword ? 'text' : 'password'}
              id="confirmPassword"
              name="confirmPassword"
              className={styles.formInput}
              required
              placeholder="Confirm Password"
              value={formData.confirmPassword}
              onChange={handleChange}
            />
            <label htmlFor="confirmPassword" className={styles.formLabel}>Confirm Password:</label>
            <button type="button" className={styles.togglePassword} onClick={toggleConfirmPasswordVisibility} tabIndex={-1}>
              <i className={`fas fa-${showConfirmPassword ? 'eye' : 'eye-slash'}`}></i>
            </button>
          </div>

          {/* Human Check */}
          <div className={styles.formField}>
            <HumanCheck 
              onValidationChange={setHumanCheckValid}
              error={errors.humanCheck}
              formType="registerOrg"
            />
          </div>

          <button type="submit" className={styles.submitButton}>
            Register Organization
          </button>
        </form>
        <div className={styles.registerFooter}>
          <span>Already have an account? <Link to="/login">Login</Link></span>
          <br />
          <span>Do you want to register as a school? <Link to="/register">Register School</Link></span>
        </div>
      </div>
    </div>
  );
};

export default RegisterOrg; 